/**
 * Customer Gold Ledger popup.
 *
 * Opened from a row on the Customers page (only for users with the
 * ``customers.gold_ledger.view`` permission). Shows every gold movement
 * RECEIVED FROM / RETURNED TO a single customer, plus a manually-recorded
 * reference rate. Users with ``customers.gold_ledger.manage`` can add IN/OUT
 * entries here; every entry also posts a matching movement into the company
 * gold stock ledger.
 *
 * Per product owner (latest): this ledger does NOT show a running balance.
 * Instead it summarises the **total gold received** and **total gold returned**
 * within the selected date period. A Gold 999 / Gold 995 toggle (identical to
 * the Goldsmith Gold Ledger) rescales every pure-gram figure
 * (``grams_995 = grams_999 / 0.995``). Purity is chosen from the company's
 * configured gold purities rather than typed as a raw percentage.
 */

import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import PdfDownloadDialog from '@/components/phase11/PdfDownloadDialog';
import { formatGrams, formatINR, formatDate, nowISTDatetimeLocal, istDatetimeLocalToISO } from '@/lib/format';
import { extractError } from '@/lib/errors';
import {
  Coins, ArrowUp, ArrowDown, X, Plus, Printer, Loader2, ArrowDownLeft, ArrowUpRight,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Gold purity view (999 ↔ 995) — mirrors the Goldsmith Gold Ledger.
// 999 is treated as 100% pure, 995 as 99.5%, so to hold the same pure gold the
// 995 figure is slightly higher: grams_995 = grams_999 / 0.995. Remembered in
// localStorage so the choice sticks across visits.
// ---------------------------------------------------------------------------
const GOLD_PURITY_KEY = 'customers.goldLedger.purityView';

function readGoldPurity() {
  try {
    return localStorage.getItem(GOLD_PURITY_KEY) === '995' ? '995' : '999';
  } catch {
    return '999';
  }
}

function convertG999(grams999, purity) {
  const n = Number(grams999) || 0;
  return purity === '995' ? n / 0.995 : n;
}

function purityUnitLabel(purity) {
  return purity === '995' ? '995' : '999';
}

// Segmented Gold 999 / Gold 995 toggle (amber-themed to match gold accents).
function GoldPurityToggle({ purity, onChange, testId = 'cgl-purity-toggle' }) {
  const opts = [
    { v: '999', label: 'Gold 999', hint: '100%' },
    { v: '995', label: 'Gold 995', hint: '99.5%' },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-amber-300 overflow-hidden text-[11px] shadow-sm"
      role="group"
      aria-label="Gold purity view"
      data-testid={testId}
    >
      {opts.map((o) => {
        const active = purity === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            aria-pressed={active}
            className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:relative ${
              active ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-amber-50'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.label} <span className="opacity-70">({o.hint})</span>
          </button>
        );
      })}
    </div>
  );
}

// Filter chronological rows to a date window + apply sort. No running balance.
function sliceRows(rows, { from, to, sort }) {
  const fromT = from ? new Date(`${from}T00:00:00`) : null;
  const toT = to ? new Date(`${to}T23:59:59.999`) : null;
  const chron = [...(rows || [])].sort(
    (a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')),
  );
  const inRange = chron.filter((r) => {
    const t = new Date(r.timestamp);
    if (fromT && t < fromT) return false;
    if (toT && t > toT) return false;
    return true;
  });
  const display = sort === 'desc' ? [...inRange].reverse() : inRange;
  return { display, hasRange: !!(fromT || toT) };
}

const NEW_ENTRY = () => ({
  direction: 'in',
  weight_grams: '',
  purity_id: '',
  rate_per_gram_999: '',
  timestamp: nowISTDatetimeLocal(),
  note: '',
  advance_gold: false,
});

// FIFO-allocate a return's pure grams (999) across the selected advance
// entries (in list order). Returns { allocations:[{entry_id, grams_999}],
// adjusted, remainder } where remainder is the balance that becomes a normal
// return against total gold received.
function computeAdvanceAllocations(entries, selectedIds, returnG999) {
  const sel = new Set(selectedIds || []);
  let left = Math.max(0, Number(returnG999) || 0);
  const allocations = [];
  let adjusted = 0;
  for (const e of (entries || [])) {
    if (left <= 1e-6) break;
    if (!sel.has(e.id)) continue;
    const rem = Math.max(0, Number(e.remaining_g999) || 0);
    const take = Math.min(rem, left);
    if (take > 1e-6) {
      const amt = Math.round(take * 1000) / 1000;
      allocations.push({ entry_id: e.id, grams_999: amt });
      adjusted += amt;
      left -= amt;
    }
  }
  adjusted = Math.round(adjusted * 1000) / 1000;
  return { allocations, adjusted, remainder: Math.round(Math.max(0, (Number(returnG999) || 0) - adjusted) * 1000) / 1000 };
}

export function CustomerGoldLedgerDialog({ open, onOpenChange, customer, companyId }) {
  const { hasPermission, effectiveCompanyId } = useAuth();
  const canManage = hasPermission('customers.gold_ledger.manage');
  const cid = companyId || effectiveCompanyId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [sortDir, setSortDir] = useState('asc');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [purity, setPurity] = useState(readGoldPurity());
  const [purities, setPurities] = useState([]);
  // Add-entry panel
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(NEW_ENTRY());
  const [saving, setSaving] = useState(false);
  // Wave 4.4 — Advance Gold
  const [advanceInfo, setAdvanceInfo] = useState({ available_advance_g999: 0, entries: [] });
  const [adjustAdvance, setAdjustAdvance] = useState(false);
  const [selectedAdvIds, setSelectedAdvIds] = useState([]);

  const customerId = customer?.id;
  const unit = purityUnitLabel(purity);

  const changePurity = (v) => {
    const next = v === '995' ? '995' : '999';
    setPurity(next);
    try { localStorage.setItem(GOLD_PURITY_KEY, next); } catch { /* ignore */ }
  };

  const load = async () => {
    if (!open || !customerId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/customers/${customerId}/gold-ledger`, {
        params: { company_id: cid },
      });
      setData(res);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load customer gold ledger'));
    } finally {
      setLoading(false);
    }
  };

  const loadPurities = async () => {
    try {
      const { data: res } = await api.get('/gold/purities', { params: { company_id: cid } });
      setPurities(Array.isArray(res) ? res : []);
    } catch (e) {
      setPurities([]);
    }
  };

  // Wave 4.4 — available advance gold + selectable entries (for return adjust).
  const loadAdvance = async () => {
    if (!customerId) return;
    try {
      const { data: res } = await api.get(`/customers/${customerId}/advance-gold`, {
        params: { company_id: cid },
      });
      setAdvanceInfo({
        available_advance_g999: Number(res?.available_advance_g999) || 0,
        entries: Array.isArray(res?.entries) ? res.entries : [],
      });
    } catch (e) {
      setAdvanceInfo({ available_advance_g999: 0, entries: [] });
    }
  };

  useEffect(() => {
    if (open) { setAdding(false); setForm(NEW_ENTRY()); setAdjustAdvance(false); setSelectedAdvIds([]); load(); loadPurities(); loadAdvance(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const rows = data?.rows || [];
  const { display, hasRange } = sliceRows(rows, { from, to, sort: sortDir });

  // Period totals (respect the selected date range + purity basis).
  const periodInG999 = display.reduce(
    (s, r) => s + (r.direction === 'in' ? (Number(r.pure_grams_999) || 0) : 0), 0,
  );
  const periodOutG999 = display.reduce(
    (s, r) => s + (r.direction === 'out' ? (Number(r.pure_grams_999) || 0) : 0), 0,
  );
  const periodIn = convertG999(periodInG999, purity);
  const periodOut = convertG999(periodOutG999, purity);
  // Wave 4.4 — Net advance received in the selected window (gross advance − adjusted).
  const periodAdvanceNetG999 = display.reduce(
    (s, r) => (r.direction === 'in' && r.advance_gold)
      ? s + (Number(r.advance_net_g999 != null ? r.advance_net_g999 : r.pure_grams_999) || 0)
      : s,
    0,
  );
  const periodAdvanceNet = convertG999(periodAdvanceNetG999, purity);
  const hasAdvanceRows = display.some((r) => r.direction === 'in' && r.advance_gold);

  const submitEntry = async () => {
    const w = Number(form.weight_grams);
    const rate = Number(form.rate_per_gram_999);
    const pur = purities.find((p) => p.id === form.purity_id);
    if (!(w > 0)) { toast.error('Enter a weight greater than 0.'); return; }
    if (!pur) { toast.error('Choose a gold purity.'); return; }
    if (!(rate > 0)) { toast.error('Enter the client-agreed rate (reference).'); return; }

    // Build advance adjustment (returns only).
    const retG999 = Math.round(w * (Number(pur.percent) / 100) * 1000) / 1000;
    const alloc = computeAdvanceAllocations(advanceInfo.entries, selectedAdvIds, retG999);
    let advancePayload = {};
    if (form.direction === 'out' && adjustAdvance) {
      if (!alloc.allocations.length) {
        toast.error('Select at least one advance entry to adjust against.');
        return;
      }
      advancePayload = {
        advance_adjustment: true,
        advance_allocations: alloc.allocations,
      };
    }

    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/gold-ledger`, {
        direction: form.direction,
        weight_grams: w,
        purity_percent: Number(pur.percent),
        purity_id: pur.id,
        purity_name: pur.name,
        rate_per_gram_999: rate,
        timestamp: istDatetimeLocalToISO(form.timestamp),
        note: form.note || '',
        company_id: cid,
        advance_gold: form.direction === 'in' ? !!form.advance_gold : false,
        ...advancePayload,
      });
      toast.success(form.direction === 'in' ? 'Gold received recorded' : 'Gold returned recorded');
      setForm(NEW_ENTRY());
      setAdding(false);
      setAdjustAdvance(false);
      setSelectedAdvIds([]);
      load();
      loadAdvance();
    } catch (e) {
      toast.error(extractError(e, 'Failed to record entry'));
    } finally {
      setSaving(false);
    }
  };

  const periodCaption = hasRange
    ? `${from ? formatDate(`${from}T00:00:00`) : 'Beginning'} – ${to ? formatDate(`${to}T00:00:00`) : 'Today'}`
    : 'All time';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="customer-gold-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins size={18} className="text-amber-600" />
            Gold Ledger — {customer?.name || 'Customer'}
          </DialogTitle>
          <DialogDescription>
            Gold received from / returned to this customer. The rate is a reference figure only — this
            gold is part of your stock and is <span className="font-medium">not</span> linked to any amount ledger.
          </DialogDescription>
        </DialogHeader>

        {/* Period summary pills (Received / Returned within the selected window) */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1" data-testid="cgl-period-caption">
            {periodCaption}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-mono tabular-nums">
            <ArrowDownLeft size={12} />
            <span className="text-[10px] uppercase tracking-wide font-sans">Received</span>
            <span className="font-semibold" data-testid="cgl-period-in">{formatGrams(periodIn)} g{unit}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[11px] font-mono tabular-nums">
            <ArrowUpRight size={12} />
            <span className="text-[10px] uppercase tracking-wide font-sans">Returned</span>
            <span className="font-semibold" data-testid="cgl-period-out">{formatGrams(periodOut)} g{unit}</span>
          </span>
          {hasAdvanceRows && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-300 bg-amber-100 text-amber-800 text-[11px] font-mono tabular-nums" title="Advance gold received in this period, net of returns adjusted against it">
              <Coins size={12} />
              <span className="text-[10px] uppercase tracking-wide font-sans">Advance (net)</span>
              <span className="font-semibold" data-testid="cgl-period-advance-net">{formatGrams(periodAdvanceNet)} g{unit}</span>
            </span>
          )}
        </div>

        {/* Controls: date range + sort + purity toggle + actions */}
        <div className="flex flex-wrap items-end justify-between gap-2 border-b border-[var(--divider)] pb-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">From</label>
              <DatePicker value={from} onValueChange={setFrom} placeholder="DD-MM-YYYY" className="h-9 w-[150px]" maxDate={to || undefined} data-testid="cgl-from" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">To</label>
              <DatePicker value={to} onValueChange={setTo} placeholder="DD-MM-YYYY" className="h-9 w-[150px]" minDate={from || undefined} data-testid="cgl-to" />
            </div>
            {(from || to) && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={() => { setFrom(''); setTo(''); }} data-testid="cgl-clear-dates">
                <X size={13} className="mr-1" /> Clear
              </Button>
            )}
            <div className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-[11px] shadow-sm" role="group" aria-label="Sort by date">
              <button type="button" onClick={() => setSortDir('asc')} aria-pressed={sortDir === 'asc'} className={`px-3 py-1.5 font-medium transition-colors ${sortDir === 'asc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="cgl-sort-asc">
                <ArrowUp size={12} className="inline mr-1 -mt-0.5" />Oldest
              </button>
              <button type="button" onClick={() => setSortDir('desc')} aria-pressed={sortDir === 'desc'} className={`px-3 py-1.5 font-medium transition-colors ${sortDir === 'desc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="cgl-sort-desc">
                <ArrowDown size={12} className="inline mr-1 -mt-0.5" />Newest
              </button>
            </div>
            <GoldPurityToggle purity={purity} onChange={changePurity} testId="cgl-purity-toggle" />
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <Button size="sm" className="h-9 luxury-button" onClick={() => setAdding((v) => !v)} data-testid="cgl-add-toggle">
                <Plus size={14} className="mr-1.5" /> Add Entry
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-9" onClick={() => setPdfOpen(true)} disabled={rows.length === 0} data-testid="cgl-print-button">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
          </div>
        </div>

        {/* Add-entry panel */}
        {canManage && adding && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3" data-testid="cgl-add-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="cgl-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Received from client</SelectItem>
                    <SelectItem value="out">Returned to client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} data-testid="cgl-date-input" />
              </div>
              <div>
                <Label>Weight (g, 3dp)</Label>
                <Input type="number" step="0.001" min="0" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} data-testid="cgl-weight-input" />
              </div>
              <div>
                <Label>Gold Purity</Label>
                <Select value={form.purity_id} onValueChange={(v) => setForm({ ...form, purity_id: v })}>
                  <SelectTrigger data-testid="cgl-purity-select">
                    <SelectValue placeholder={purities.length ? 'Choose purity' : 'No purities configured'} />
                  </SelectTrigger>
                  <SelectContent>
                    {purities.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`cgl-purity-option-${p.id}`}>
                        {p.name} ({Number(p.percent).toFixed(2)}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {purities.length === 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">Add purities under Gold → Purities first.</p>
                )}
              </div>
              <div>
                <Label>Rate (₹/g 999) <span className="text-rose-600">*</span> <span className="text-slate-400">(reference)</span></Label>
                <Input type="number" step="0.01" min="0" value={form.rate_per_gram_999} onChange={(e) => setForm({ ...form, rate_per_gram_999: e.target.value })} placeholder="Client-agreed rate" data-testid="cgl-rate-input" />
              </div>
              <div>
                <Label>Note</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. advance for order OF-123" data-testid="cgl-note-input" />
              </div>
            </div>

            {/* Wave 4.4 — Advance Gold (on received entries) */}
            {form.direction === 'in' && (
              <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-100/50 px-3 py-2 cursor-pointer select-none" data-testid="cgl-advance-row">
                <Checkbox checked={!!form.advance_gold} onCheckedChange={(v) => setForm({ ...form, advance_gold: !!v })} className="mt-0.5" data-testid="cgl-advance-checkbox" />
                <span className="text-xs text-[var(--ink-navy)]">
                  <span className="font-semibold inline-flex items-center gap-1"><Coins size={12} /> Advance Gold</span>
                  <span className="block text-[11px] text-slate-500">Record this receipt as advance gold received from the client (shown in the Advance Gold report).</span>
                </span>
              </label>
            )}

            {/* Wave 4.4 — Adjust a return against advance gold received */}
            {form.direction === 'out' && advanceInfo.available_advance_g999 > 0 && (() => {
              const pur = purities.find((p) => p.id === form.purity_id);
              const retG999 = pur ? Math.round((Number(form.weight_grams) || 0) * (Number(pur.percent) / 100) * 1000) / 1000 : 0;
              const alloc = computeAdvanceAllocations(advanceInfo.entries, selectedAdvIds, retG999);
              return (
                <div className="rounded-md border border-amber-300 bg-amber-100/40 p-3 space-y-2" data-testid="cgl-adjust-advance-block">
                  <label className="flex flex-wrap items-center gap-2 cursor-pointer select-none">
                    <Checkbox checked={adjustAdvance} onCheckedChange={(v) => { setAdjustAdvance(!!v); if (!v) setSelectedAdvIds([]); }} data-testid="cgl-adjust-advance-toggle" />
                    <span className="text-xs font-semibold text-[var(--ink-navy)] inline-flex items-center gap-1"><Coins size={12} /> Adjust this return against advance gold received</span>
                    <span className="text-[11px] text-slate-500">(available {formatGrams(convertG999(advanceInfo.available_advance_g999, purity))} g{unit})</span>
                  </label>
                  {adjustAdvance && (
                    <div className="space-y-1.5">
                      <div className="text-[11px] text-slate-500">Select the advance entries to consume. The return is applied across your selection (oldest first); any balance beyond them is a normal return.</div>
                      <div className="max-h-40 overflow-y-auto rounded border border-amber-200 divide-y divide-amber-100 bg-white">
                        {advanceInfo.entries.map((e) => (
                          <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-amber-50" data-testid={`cgl-adv-entry-${e.id}`}>
                            <Checkbox
                              checked={selectedAdvIds.includes(e.id)}
                              onCheckedChange={(v) => setSelectedAdvIds((prev) => v ? [...prev, e.id] : prev.filter((x) => x !== e.id))}
                              data-testid={`cgl-adv-entry-check-${e.id}`}
                            />
                            <span className="font-mono text-slate-700 whitespace-nowrap">{formatDate(e.timestamp)}</span>
                            <span className="text-slate-400">·</span>
                            <span className="font-mono tabular-nums text-amber-800">{formatGrams(convertG999(e.remaining_g999, purity))} g{unit} left</span>
                            {e.note && <span className="text-slate-400 truncate max-w-[160px]">· {e.note}</span>}
                          </label>
                        ))}
                      </div>
                      <div className="text-[11px] font-medium text-[var(--ink-navy)]" data-testid="cgl-adjust-advance-summary">
                        Adjusting <span className="text-amber-800 font-semibold">{formatGrams(convertG999(alloc.adjusted, purity))} g{unit}</span> against advance
                        {alloc.remainder > 1e-6 && <> · <span className="text-rose-700">{formatGrams(convertG999(alloc.remainder, purity))} g{unit}</span> as a normal return</>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setForm(NEW_ENTRY()); setAdjustAdvance(false); setSelectedAdvIds([]); }} data-testid="cgl-add-cancel">Cancel</Button>
              <Button size="sm" className="luxury-button" onClick={submitEntry} disabled={saving} data-testid="cgl-add-submit">
                {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Save Entry
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="ledger-table overflow-x-auto rounded-lg border border-[var(--divider)]">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-500"><Loader2 size={18} className="inline animate-spin mr-2" /> Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Date</th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Type</th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Weight (g)</th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Purity</th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50" data-testid="cgl-pure-col-header">Pure (g{unit})</th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Rate (ref)</th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Value (ref)</th>
                </tr>
              </thead>
              <tbody data-testid="cgl-table-body">
                {display.map((r, idx) => {
                  const isIn = r.direction === 'in';
                  const pureDisp = convertG999(r.pure_grams_999 || 0, purity);
                  return (
                    <tr key={r.id || idx} className="border-t border-[var(--divider)] hover:bg-slate-50/40" data-testid={`cgl-row-${idx}`}>
                      <td className="px-3 py-2 text-xs text-slate-700 font-mono whitespace-nowrap">{formatDate(r.timestamp)}</td>
                      <td className="px-3 py-2">
                        {isIn ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-medium">
                            <ArrowDownLeft size={11} /> Received
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-medium">
                            <ArrowUpRight size={11} /> Returned
                          </span>
                        )}
                        {isIn && r.advance_gold && (
                          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-medium" data-testid={`cgl-advance-badge-${idx}`}>
                            <Coins size={10} /> Advance
                          </span>
                        )}
                        {!isIn && r.advance_adjustment && (
                          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium" title={`${formatGrams(convertG999(r.advance_adjusted_g999 || 0, purity))} g${unit} adjusted against advance`} data-testid={`cgl-adjust-badge-${idx}`}>
                            <Coins size={10} /> Adj. vs advance
                          </span>
                        )}
                        {isIn && r.advance_gold && Number(r.advance_consumed_g999) > 0 && (
                          <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`cgl-advance-net-${idx}`}>
                            Net advance: {formatGrams(convertG999(r.advance_net_g999 != null ? r.advance_net_g999 : (r.pure_grams_999 - (r.advance_consumed_g999 || 0)), purity))} g{unit}
                            <span className="text-slate-400"> (−{formatGrams(convertG999(r.advance_consumed_g999, purity))} adjusted)</span>
                          </div>
                        )}
                        {r.note && <div className="text-[10px] text-slate-500 mt-0.5 max-w-[240px] truncate">{r.note}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-slate-700">{formatGrams(r.weight_grams || 0)}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {r.purity_name ? `${r.purity_name} (${Number(r.purity_percent || 0).toFixed(2)}%)` : `${Number(r.purity_percent || 0).toFixed(2)}%`}
                      </td>
                      <td className={`px-3 py-2 text-xs text-right font-mono tabular-nums ${isIn ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {isIn ? '+' : '−'}{formatGrams(pureDisp)}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-slate-600">{formatINR(r.rate_per_gram_999 || 0)}</td>
                      <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-slate-600">{formatINR(r.value || 0)}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-12 text-center text-sm text-slate-500" data-testid="cgl-empty">
                      No gold received from this customer yet.{canManage ? ' Use "Add Entry" to record some.' : ''}
                    </td>
                  </tr>
                )}
                {rows.length > 0 && hasRange && display.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-xs text-slate-500" data-testid="cgl-range-empty">
                      No entries in the selected date range.
                    </td>
                  </tr>
                )}
              </tbody>
              {display.length > 0 && (
                <tfoot>
                  <tr className="bg-amber-50/60 border-t-2 border-amber-200" data-testid="cgl-totals-row">
                    <td className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-900" colSpan={4}>
                      Period Totals
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono tabular-nums font-semibold text-amber-900">
                      <span className="text-emerald-700">+{formatGrams(periodIn)}</span>
                      {' / '}
                      <span className="text-rose-700">−{formatGrams(periodOut)}</span>
                    </td>
                    <td className="px-3 py-2" colSpan={2}>
                      <span className="text-[10px] text-slate-500">Received / Returned (g{unit})</span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>

        <PdfDownloadDialog
          open={pdfOpen}
          onOpenChange={setPdfOpen}
          title={`Gold Ledger — ${customer?.name || 'Customer'}`}
          description="A4 landscape PDF (Received / Returned, with period totals). Rate shown as reference."
          companyId={cid}
          endpoint={`/customers/${customerId}/gold-ledger/pdf`}
          extraParams={{ sort: sortDir, date_from: from || '', date_to: to || '', purity }}
          filename={`Customer_Gold_Ledger_${(customer?.name || 'customer').replace(/\s+/g, '_')}.pdf`}
          mode="download"
        />
      </DialogContent>
    </Dialog>
  );
}

export default CustomerGoldLedgerDialog;
